import {
  ACTION,
  Dialog,
  Flex,
  OrderStatus,
  Patch,
  abbreviateId,
  useUserContext,
  type DialogProps,
  type Order,
  type UseDisclosureReturn,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Autocomplete, FormError } from '@src/components/Form';

import { CancelDialogWrapper, Row } from './styles';

import { sortAutocompleteItemsFn } from '@src/components/Form/Autocomplete';
import { useGroups, useRoleAuth } from '@src/hooks';
import { DatadogTradingAction, trackOrderAction } from '@src/utils/datadogTradingActions';

interface BulkModifyGroupDialogProps extends DialogProps, UseDisclosureReturn {
  selectedOrders?: Order[];
}

export const BulkModifyGroupDialog = ({ selectedOrders, ...props }: BulkModifyGroupDialogProps) => {
  if (!selectedOrders?.length) {
    return null;
  }

  if (selectedOrders.length === 1) {
    return ModifyGroupDialog({ selectedOrder: selectedOrders[0], ...props });
  }

  return null; // No bulk group edit yet
};

interface ModifyGroupDialogProps extends DialogProps, UseDisclosureReturn {
  selectedOrder?: Order;
}

interface Group {
  id: string;
  label: string;
}

export const ModifyGroupDialog = ({ selectedOrder, ...props }: ModifyGroupDialogProps) => {
  const [modifyGroupError, setModifyGroupError] = useState<string | undefined>();
  const [group, setGroup] = useState<string>();
  const { orgApiEndpoint } = useUserContext();
  const { isAuthorized } = useRoleAuth();

  const groups = useGroups();
  const groupOptions = useMemo(() => {
    return groups.map(group => ({
      id: group,
      label: group,
    }));
  }, [groups]);

  useEffect(() => {
    setGroup(selectedOrder?.Group || '');
  }, [selectedOrder]);
  const { open, close } = props;
  const modifyGroup = useCallback(
    (orderID: string, group?: string) => {
      trackOrderAction(DatadogTradingAction.Modify, { orderID });
      Patch(orgApiEndpoint ?? '', `/orders/${orderID}`, { Group: group })
        .then(() => {
          close();
        })
        .catch((e: ErrorEvent) => {
          setModifyGroupError(e?.message || 'Something went wrong.');
          open();
        });
    },
    [open, close, orgApiEndpoint]
  );

  const handleOnGroupModifyDialogConfirm = useCallback(() => {
    if (!selectedOrder) {
      return;
    }
    modifyGroup(selectedOrder?.OrderID, group);
  }, [group, modifyGroup, selectedOrder]);

  if (!selectedOrder) {
    return null;
  }

  return (
    <Dialog
      {...props}
      onConfirm={handleOnGroupModifyDialogConfirm}
      confirmLabel="Update"
      confirmDisabled={!isAuthorized(ACTION.SUBMIT_ORDER)}
      cancelLabel="Cancel"
      contentOverflow="visible"
      title="Modify Group"
      width={300}
      stretchButtons
    >
      <CancelDialogWrapper>
        <Row>
          <h3>
            Order #<b>{abbreviateId(selectedOrder.OrderID)}</b>
          </h3>
          <OrderStatus
            ordStatus={selectedOrder.OrdStatus}
            decisionStatus={selectedOrder.DecisionStatus}
            cumQty={selectedOrder.CumQty}
            orderQty={selectedOrder.OrderQty}
            pricingMode={selectedOrder.PricingMode}
            tooltipText={selectedOrder.Text}
            isCustomerOrder={false}
          />
        </Row>
        <Row>
          <Flex flex={1} w="100%" justifyContent="space-between" alignItems="center" gap="spacing32" mt="spacing16">
            <span>Group</span>
            <div style={{ flexGrow: 1 }}>
              <Autocomplete
                getItemValue={(item: Group) => item.label}
                inputProps={{
                  id: 'group',
                  autoFocus: false,
                  type: 'text',
                }}
                items={groupOptions}
                isCentered={false}
                shouldItemRender={(item: Group, value: string) => item.label.indexOf(value) !== -1}
                value={group}
                sortItems={sortAutocompleteItemsFn}
                onChange={(e, val) => setGroup(val)}
                onSelect={val => setGroup(val)}
                style={{ textAlign: 'left' }}
              />
            </div>
          </Flex>
        </Row>
        {modifyGroupError && <FormError style={{ justifyContent: 'flex-end' }}>{modifyGroupError}</FormError>}
      </CancelDialogWrapper>
    </Dialog>
  );
};
